import { Box, Hide, Stack } from '@chakra-ui/react';
import { PressMarquee } from '@time-line/components/blocks';
import { Divider, Grid, ProductAccordion } from '@time-line/components/common';
import { PageModuleResolver } from 'components/common/PageModuleResolver';
import { PageWrapper } from 'components/common/PageWrapper';
import {
  ProductBulletPoints,
  RichTextResolver,
} from 'components/common/RichTextResolver';
import { ProductBundles } from 'components/product/ProductBundles';
import { ProductCheckmarks } from 'components/product/ProductCheckmarks';
import { ProductComplementary } from 'components/product/ProductComplementary';
import { ProductConfiguratorRender } from 'components/product/ProductConfiguratorRender';
import { ProductDisclaimer } from 'components/product/ProductDisclaimer';
import { ProductFaq } from 'components/product/ProductFaq';
import { ProductFrontrowMdWidget } from 'components/product/ProductFrontrowMdWidget';
import { ProductGalleryDesktop } from 'components/product/ProductGallery/ProductGalleryDesktop';
import { ProductGalleryMobile } from 'components/product/ProductGallery/ProductGalleryMobile';
import { ProductIntro } from 'components/product/ProductIntro';
import { ProductSeeItInAction } from 'components/product/ProductSeeItInAction';
import { ProductSeo } from 'components/product/ProductSeo';
import { ProductReviewsBlock } from 'components/reviews/ProductReviewsBlock';
import { FrontrowMDClinicianAI } from 'components/sections/FrontrowMdClinicianAI';
import { FrontrowMDTestimonials } from 'components/sections/FrontrowMdTestimonials';
import { ProductCardCarousel } from 'components/sections/ProductCardCarousel';
import { useProductPage } from 'hooks/pages/useProductPage';
import { useProductCollection } from 'hooks/product/useProductCollection';
import { useSanityProduct } from 'hooks/product/useSanityProduct';
import { useShopifyProduct } from 'hooks/product/useShopifyProduct';
import { useReviewedProducts } from 'hooks/reviews/useReviewedProducts';
import { useIsSkincare } from 'hooks/useIsSkincare';
import { useLocalStorage, UserLocale } from 'hooks/userPreferences';
import { env } from 'lib/constants/env';
import { SCIENCE, SKINCARE_SCIENCE } from 'lib/constants/routes';
import NextLink from 'next/link';
import { isTruemedCountry } from 'utils/localisation/isTruemedCountry';
import { getGalleryPriceBadge } from 'utils/product/getGalleryPriceBadge';
import { getIngredientsAccordionCopy } from 'utils/product/getIngredientsAccordionCopy';
import { getPdpPurchaseType } from 'utils/product/pdpPurchaseConfig';
import { resolveProductInfo } from 'utils/product/resolveProductInfo';
import { removeExcludedProducts } from 'utils/removeExcludedProducts';
import { getReviewProductSlugs } from 'utils/reviews/getReviewProductSlugs';

interface ProductProps {
  slug: string;
}

export function Product({ slug }: ProductProps) {
  const { data: productPage } = useProductPage(slug);
  const {
    value: { countryCode },
  } = useLocalStorage(UserLocale);
  const isSkincare = useIsSkincare();

  const {
    mostPopularVariantId,
    bestValueVariantId,
    media,
    longDescription,
    relatedProducts,
    alternativeProduct,
    complementaryProducts,
    blocks,
    bundleProducts,
    faq,
    productType,
    pressQuotes,
    bulletPoints,
    disclaimer,
    howToUseV2,
    benefits,
    ingredients,
    informationPanel,
    frontrowMD,
    seeItInAction,
    productFormats,
  } = productPage;

  const product = useSanityProduct(slug, { required: true });

  const {
    title,
    altTitle,
    productBullet,
    secondaryProductBullet,
    isPreorder,
    infoBox,
    isTruemed,
    isMoneyBackGuarantee,
    isDoctorRecommended,
    isNewPrice,
    category,
  } = product;

  const bundleCollection = bundleProducts
    ? {
        products: bundleProducts.map((item) => item.product),
      }
    : undefined;

  const reviewProductSlugs = getReviewProductSlugs(bundleProducts, slug);

  const parentProducts = useProductCollection(relatedProducts);
  const bundle = useProductCollection(bundleCollection);

  const bundleFreeBySlug = new Map(
    bundleProducts?.map((item) => [item.product.slug, item.isFree]),
  );
  const bundleVariantGidBySlug = new Map(
    bundleProducts?.map((item) => [item.product.slug, item.variant?.gid]),
  );
  const bundleItems = bundle.map((bundleProduct) => ({
    product: bundleProduct,
    isFree: bundleFreeBySlug.get(bundleProduct.slug) ?? false,
    variantGid: bundleVariantGidBySlug.get(bundleProduct.slug),
  }));

  const { data: shopifyProduct, isLoading: isProductLoading } =
    useShopifyProduct(slug);
  const isProductUnavailable = shopifyProduct === null;

  const { data: reviewedProducts } = useReviewedProducts({
    slugsToFilter: reviewProductSlugs,
  });

  const altCollectionWithoutCurrentProduct = parentProducts
    ? removeExcludedProducts(parentProducts, [slug])
    : null;

  const otpVariant = shopifyProduct?.variants[0];
  const discountBadge = getGalleryPriceBadge({
    isNewPrice,
    price: otpVariant?.price,
    compareAtPrice: otpVariant?.compareAtPrice,
  });

  const productGalleryProps = {
    title,
    media,
    isDoctorRecommended,
    discountBadge,
  };

  const productInfo = resolveProductInfo({
    sanityProduct: product,
    productOverrides: altTitle,
  });

  return (
    <PageWrapper mt={0}>
      {shopifyProduct && (
        <ProductSeo slug={slug} shopifyProduct={shopifyProduct} />
      )}
      <Grid className="section--product" pt={{ base: 0, lg: 'lg' }} mb="2xl">
        {/* INFO: Use Hide and `display: none` to prevent FOUC before Hide
         * is mounted and can actually remove the child from the DOM. */}
        <Hide above="lg">
          <Box gridColumn="full" display={{ base: 'block', lg: 'none' }}>
            <ProductGalleryMobile {...productGalleryProps} />
          </Box>
        </Hide>
        <Grid.Inner gridColumn="main" gridRowGap="lg">
          <Hide below="lg">
            <Box
              gridColumn={{
                base: 'span 7',
                xl: 'span 8',
              }}
              width="100%"
              pr="lg"
              display={{ base: 'none', lg: 'block' }}
            >
              <ProductGalleryDesktop {...productGalleryProps} />
            </Box>
          </Hide>
          <Box
            display="flex"
            flexDirection="column"
            gap="m"
            gridColumn={{ base: 'span 12', lg: '8 / span 6', xl: '9 / span 5' }}
            gridRow={{ base: 2, lg: 1 }}
            position="sticky"
            top="lg"
            alignSelf="start"
          >
            <Stack gap="sm">
              <ProductIntro
                title={productInfo.title}
                subtitle={productInfo.subtitle}
                longDescription={longDescription}
                productBullet={productBullet}
                secondaryProductBullet={secondaryProductBullet}
                isPreorder={isPreorder}
                showReviews
                productSlugs={reviewProductSlugs}
                isMoneyBackGuarantee={
                  !isProductUnavailable && isMoneyBackGuarantee
                }
              />
              {bulletPoints && (
                <Box>
                  <RichTextResolver
                    blocks={bulletPoints}
                    container={ProductBulletPoints}
                  />
                </Box>
              )}
              {!!bundleItems.length && <ProductBundles items={bundleItems} />}
              <ProductConfiguratorRender
                slug={slug}
                shopifyProduct={shopifyProduct}
                isPreorder={isPreorder}
                infoBox={infoBox}
                isLoading={isProductLoading}
                mostPopularVariantId={mostPopularVariantId}
                bestValueVariantId={bestValueVariantId}
                purchaseType={getPdpPurchaseType(category)}
                productFormats={productFormats}
                showStickyCta
              />

              {!isProductLoading && (
                <ProductCheckmarks
                  productId={shopifyProduct?.id}
                  isShippingInfoVisible
                  isTruemedVisible={isTruemed && isTruemedCountry(countryCode)}
                />
              )}

              {disclaimer && (
                <ProductDisclaimer>{disclaimer}</ProductDisclaimer>
              )}
            </Stack>

            {env.NEXT_PUBLIC_FRONTROWMD_ENABLED &&
              frontrowMD?.productId &&
              frontrowMD?.isFrontrowBadgeEnabled && (
                <>
                  <Divider />
                  <ProductFrontrowMdWidget id={frontrowMD.productId} />
                </>
              )}

            {(howToUseV2 ||
              benefits ||
              ingredients ||
              !!seeItInAction?.items?.length) && (
              <Box>
                {(howToUseV2 || benefits || ingredients) && (
                  <ProductAccordion
                    title={title}
                    howToUseV2={howToUseV2}
                    benefits={benefits}
                    ingredients={ingredients}
                    informationPanel={informationPanel}
                    scienceHref={isSkincare ? SKINCARE_SCIENCE : SCIENCE}
                    {...getIngredientsAccordionCopy(productType)}
                    RichTextResolver={RichTextResolver}
                  />
                )}

                {!!seeItInAction?.items?.length && (
                  <ProductSeeItInAction {...seeItInAction} />
                )}
              </Box>
            )}

            {alternativeProduct && (
              <ProductComplementary products={[alternativeProduct.product]} />
            )}

            {complementaryProducts && complementaryProducts.products && (
              <ProductComplementary
                title={complementaryProducts.title}
                products={complementaryProducts.products}
                ctaType="addToCart"
              />
            )}

            {env.NEXT_PUBLIC_FRONTROWMD_ENABLED &&
              frontrowMD?.isFrontrowClinicianAIEnabled &&
              frontrowMD?.productId && (
                <FrontrowMDClinicianAI id={frontrowMD?.productId} />
              )}
          </Box>
        </Grid.Inner>
      </Grid>

      {blocks && (
        <PageModuleResolver
          blocks={blocks}
          pageTitle={title}
          blockRules={{ isDividerVisible: () => true }}
        />
      )}

      {env.NEXT_PUBLIC_FRONTROWMD_ENABLED &&
        frontrowMD?.isFrontrowTestimonialsEnabled &&
        frontrowMD?.productId && (
          <FrontrowMDTestimonials id={frontrowMD?.productId} />
        )}

      {!!reviewedProducts?.length && (
        <ProductReviewsBlock productSlugs={reviewProductSlugs} />
      )}

      {pressQuotes?.quotes && <PressMarquee quotes={pressQuotes.quotes} />}

      {faq?.list && <ProductFaq {...faq} />}

      {relatedProducts &&
        altCollectionWithoutCurrentProduct &&
        altCollectionWithoutCurrentProduct?.length > 0 && (
          <ProductCardCarousel
            title={relatedProducts.title}
            cta={
              relatedProducts?.plp
                ? {
                    title:
                      relatedProducts.plp?.ctaLabel ||
                      `Shop ${relatedProducts.plp?.label}` ||
                      '',
                    href: relatedProducts.plp.href,
                    as: NextLink,
                  }
                : undefined
            }
            products={altCollectionWithoutCurrentProduct}
            collectionName={relatedProducts.plp?.slug}
          />
        )}
    </PageWrapper>
  );
}
